import { arc, type Arc } from 'd3-shape';
import { d3 } from './d3';

const DEFAULT_INNER_RADIUS = 120;
const DEFAULT_OUTER_RADIUS = 160;

interface ArcDatum {
  endAngle: number;
}

interface T {
  selector: string;
  backgroundColor: string;
  fillColors: string[];
  layout: { width: number; height: number };
}

/**
 * CountdownClock: countdown visualisation for quiz lobby
 */
export class CountdownClock implements T {
  readonly selector: string;
  readonly backgroundColor: string;
  readonly fillColors: string[];
  readonly layout: { width: number; height: number };
  clock: d3.Selection<SVGGElement, ArcDatum, HTMLElement, unknown> | undefined;
  foreground:
    | d3.Selection<SVGPathElement, ArcDatum, d3.BaseType, unknown>
    | undefined;
  arc: Arc<this, ArcDatum> = arc<ArcDatum>();

  /**
   * @param selector: string selection to a dom node, e.g., #countdownClock-${questionId}
   * @param backgroundColor: color string, e.g., #e8e8e8
   * @param fillColors: array of colors for bars, e.g., ['green', 'yellow', 'blue']
   * @param layout: width and height of container
   */
  constructor(props: T) {
    this.selector = props.selector;
    this.backgroundColor = props.backgroundColor;
    this.fillColors = props.fillColors;
    this.layout = props.layout;
  }

  /**
   * Adds a tween for a transition’s "d" attribute on an arc from the current angle to the new angle.
   *
   * @param transition d3 transition
   * @param newAngle the new angle
   */
  private arcTween = (
    transition: d3.Transition<SVGPathElement, ArcDatum, d3.BaseType, unknown>,
    newAngle: number,
  ): void => {
    transition.attrTween('d', (d) => {
      const interpolate = d3.interpolate(newAngle, d.endAngle);
      return (t) => {
        d.endAngle = interpolate(t);
        return this.arc(d) ?? '';
      };
    });
  };

  /**
   * Starts clock animation
   *
   * @param duration animation duration (ms)
   */
  public start = (duration: number): void => {
    if (this.foreground) {
      this.foreground
        .transition()
        .ease(d3.easeLinear)
        .duration(duration)
        .call(this.arcTween, 2 * Math.PI);
    }
  };

  /**
   * Initializes the countdown clock
   */
  public buildClock = (): void => {
    this.clock = d3.select(this.selector);
    this.arc
      .innerRadius(DEFAULT_INNER_RADIUS)
      .outerRadius(DEFAULT_OUTER_RADIUS)
      .startAngle(0);

    this.clock
      .attr(
        'transform',
        `translate(${this.layout.width / 2}, ${this.layout.height / 2})`,
      )
      .append('path')
      .datum({ endAngle: 2 * Math.PI })
      .attr('fill', this.backgroundColor)
      .attr('class', 'background')
      .attr('d', (d) => this.arc(d));
    this.foreground = this.clock
      .append('path')
      .datum({ endAngle: 0 })
      .attr('fill', this.fillColors[0] ?? 'black')
      .attr('class', 'foreground')
      .attr('d', (d) => this.arc(d));
  };
}
